import React, { useState } from 'react';
import { X, Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APIRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    use_case: '',
    expected_volume: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      // Format the message for the email
      const emailMessage = `
API Access Request

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Phone: ${formData.phone || 'Not provided'}

What do you plan to use the API for?
${formData.use_case}

Expected monthly API volume:
${formData.expected_volume}

Additional message:
${formData.message || 'None'}
      `.trim();

      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: emailMessage
      });
      
      if (response.data.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          use_case: '',
          expected_volume: '',
          message: ''
        });
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          setStatus('idle');
          onClose();
        }, 3000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to send request. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to send request. Please try emailing us directly at joe@zeusweatherllc.com');
      console.error('API request form error:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-8">
          {status === 'success' ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h2>
              <p className="text-slate-600 mb-6">
                Thank you for your interest in Zeus Weather API. We'll review your request and get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Request API Access</h2>
                <p className="text-slate-600">
                  Tell us about your project and we'll get you set up with API access.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-slate-900 font-semibold mb-2">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-900 font-semibold mb-2">
                      Work Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company" className="text-slate-900 font-semibold mb-2">
                      Company Name *
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-slate-900 font-semibold mb-2">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="use_case" className="text-slate-900 font-semibold mb-2">
                    What do you plan to use the API for? *
                  </Label>
                  <Textarea
                    id="use_case"
                    name="use_case"
                    required
                    value={formData.use_case}
                    onChange={handleChange}
                    placeholder="e.g., Insurance claims validation, roofing lead generation, property risk assessment..."
                    rows={4}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="expected_volume" className="text-slate-900 font-semibold mb-2">
                    Expected Monthly API Volume *
                  </Label>
                  <select
                    id="expected_volume"
                    name="expected_volume"
                    required
                    value={formData.expected_volume}
                    onChange={handleChange}
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select volume range</option>
                    <option value="1k-10k">1,000 - 10,000 requests</option>
                    <option value="10k-50k">10,000 - 50,000 requests</option>
                    <option value="50k-100k">50,000 - 100,000 requests</option>
                    <option value="100k-500k">100,000 - 500,000 requests</option>
                    <option value="500k+">500,000+ requests</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-900 font-semibold mb-2">
                    Additional Information
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Any specific requirements or questions?"
                    rows={3}
                    className="mt-2"
                  />
                </div>

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {errorMessage}
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={status === 'sending'}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                  >
                    {status === 'sending' ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="mr-2 h-5 w-5" />
                        Submit Request
                      </span>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="px-8"
                  >
                    Cancel
                  </Button>
                </div>

                <p className="text-sm text-slate-500 text-center">
                  We typically respond within 24 hours. For urgent inquiries, email{' '}
                  <a href="mailto:joe@zeusweatherllc.com" className="text-blue-600 hover:text-blue-700">
                    joe@zeusweatherllc.com
                  </a>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default APIRequestModal;
